import { computed, Injectable, signal } from '@angular/core';

import { Ucum } from '@treetop/shared/model';

export type ViewMode =
  | 'smartbook'
  | 'preview'
  | 'preview-dialog'
  | 'review'
  | 'publishReview'
  | 'editImportedGuideline'
  | 'merge'
  | 'teaser';

export interface SmartbookContentOptions {
  isPublicPage: boolean;
  isRevisionDateEnabled: boolean;
  mode: ViewMode;
  soonToExpirePeriod: Ucum | undefined;
  showTags?: boolean;
}

@Injectable()
export class GuidelineViewService {
  private _mode = signal<ViewMode>('smartbook');

  private _isPublicPage = signal<boolean>(false);
  private _isRevisionDateEnabled = signal<boolean>(false);
  private _showTags = signal<boolean>(false);
  private _soonToExpirePeriod = signal<Ucum | undefined>(undefined);

  public readonly mode = this._mode.asReadonly();

  public readonly isPublicPage = this._isPublicPage.asReadonly();
  public readonly isRevisionDateEnabled = this._isRevisionDateEnabled.asReadonly();
  public readonly showTags = this._showTags.asReadonly();
  public readonly soonToExpirePeriod = this._soonToExpirePeriod.asReadonly();

  public readonly isPreviewMode = computed(() => this._mode() === 'preview' || this._mode() === 'preview-dialog');
  public readonly isPreviewDialogMode = computed(() => this._mode() === 'preview-dialog');
  public readonly isReviewMode = computed(() => this._mode() === 'review');
  public readonly isPublishReviewMode = computed(
    () => this._mode() === 'publishReview' || this._mode() === 'editImportedGuideline' || this._mode() === 'merge',
  );
  public readonly isEditImportedGuidelineMode = computed(() => this._mode() === 'editImportedGuideline');
  public readonly isMergeMode = computed(() => this._mode() === 'merge');
  public readonly isSmartbookMode = computed(() => this._mode() === 'smartbook' || this._mode() === 'teaser');
  public readonly isTeaserMode = computed(() => this._mode() === 'teaser');
  public readonly allowEditTags = computed(
    () => this._mode() === 'publishReview' || this._mode() === 'merge' || this._mode() === 'editImportedGuideline',
  );

  public init(options: Partial<SmartbookContentOptions>): void {
    if (options.isPublicPage !== undefined) {
      this._isPublicPage.set(options.isPublicPage);
    }

    if (options.isRevisionDateEnabled !== undefined) {
      this._isRevisionDateEnabled.set(options.isRevisionDateEnabled);
    }

    if (options.mode !== undefined) {
      this._mode.set(options.mode);
    }

    if (options.showTags !== undefined) {
      this._showTags.set(options.showTags);
    }

    if (options.soonToExpirePeriod !== undefined) {
      this._soonToExpirePeriod.set(options.soonToExpirePeriod);
    }
  }

  public setShowTags(showTags: boolean): void {
    this._showTags.set(showTags);
  }
}
